import { useState, useEffect } from 'react'
import api from '../services/api'
import { useAuthStore } from '../store/authStore'
import toast from 'react-hot-toast'
import {
  Bell, MessageSquare, Mail, Send, CheckCheck, Check,
  ChevronDown, ChevronUp, X, Info, AlertCircle, AlertTriangle,
  Megaphone, Reply, Search
} from 'lucide-react'
import LoadingSpinner from './LoadingSpinner'

const TYPE_ICON = {
  info: Info, warning: AlertTriangle, alert: AlertCircle,
  success: Check, announcement: Megaphone
}
const TYPE_COLOR = {
  info: 'bg-blue-500/10 text-blue-400',
  warning: 'bg-cinema-glow/10 text-cinema-glow',
  alert: 'bg-red-500/10 text-red-400',
  success: 'bg-green-500/10 text-green-400',
  announcement: 'bg-purple-500/10 text-purple-400'
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const now = new Date()
  const diff = Math.floor((now - d) / 1000)
  if (diff < 60) return 'Just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * Reusable messages + notifications page.
 * Props:
 *   contactsEndpoint  — API path to GET contacts for composing (e.g. '/trainers/contacts')
 *   title             — Page heading
 */
export default function MessagesPage({ contactsEndpoint, title = 'Messages & Notifications' }) {
  const { user } = useAuthStore()
  const [tab, setTab] = useState('notifications')
  const [notifications, setNotifications] = useState([])
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(null)

  // Compose
  const [composing, setComposing] = useState(false)
  const [replyTo, setReplyTo] = useState(null)
  const [composeData, setComposeData] = useState({ recipient_id: '', subject: '', message: '' })
  const [sending, setSending] = useState(false)
  const [contacts, setContacts] = useState([])

  // Filters
  const [msgFilter, setMsgFilter] = useState('all')
  const [msgSearch, setMsgSearch] = useState('')

  useEffect(() => { fetchAll(); fetchContacts() }, [])

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [notifRes, msgRes] = await Promise.all([
        api.get('/communication/notifications'),
        api.get('/communication/messages')
      ])
      setNotifications(notifRes.data)
      setMessages(msgRes.data)
    } catch {
      toast.error('Failed to load messages')
    } finally {
      setLoading(false)
    }
  }

  const fetchContacts = async () => {
    if (!contactsEndpoint) return
    try {
      const res = await api.get(contactsEndpoint)
      setContacts(res.data || [])
    } catch { /* non-critical */ }
  }

  const markNotifRead = async (id) => {
    try {
      await api.put(`/communication/notifications/${id}/read`)
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
    } catch {}
  }

  const markAllRead = async () => {
    try {
      await api.put('/communication/notifications/mark-all-read')
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      toast.success('All notifications marked as read')
    } catch { toast.error('Failed') }
  }

  const markMsgRead = async (id) => {
    try {
      await api.put(`/communication/messages/${id}/read`)
      setMessages(prev => prev.map(m => m.id === id ? { ...m, is_read: true } : m))
    } catch {}
  }

  const toggleExpand = (id, type) => {
    const key = `${type}-${id}`
    if (expanded === key) { setExpanded(null); return }
    setExpanded(key)
    if (type === 'notif') markNotifRead(id)
    if (type === 'msg') markMsgRead(id)
  }

  const handleReply = (msg) => {
    setReplyTo(msg)
    setComposeData({
      recipient_id: msg.sender_id === user.id ? msg.recipient_id : msg.sender_id,
      subject: msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject}`,
      message: ''
    })
    setComposing(true)
  }

  const handleSend = async (e) => {
    e.preventDefault()
    if (!composeData.recipient_id || !composeData.message.trim()) {
      toast.error('Please fill in recipient and message'); return
    }
    setSending(true)
    try {
      await api.post('/communication/messages', {
        recipient_id: parseInt(composeData.recipient_id),
        subject: composeData.subject || '(no subject)',
        message: composeData.message,
        parent_message_id: replyTo?.id || null
      })
      toast.success('Message sent!')
      setComposing(false); setReplyTo(null)
      setComposeData({ recipient_id: '', subject: '', message: '' })
      fetchAll()
    } catch { toast.error('Failed to send message') }
    finally { setSending(false) }
  }

  const unreadNotifs = notifications.filter(n => !n.is_read).length
  const unreadMsgs = messages.filter(m => m.recipient_id === user?.id && !m.is_read).length

  const filteredMessages = messages.filter(msg => {
    const isReceived = msg.recipient_id === user?.id
    const otherName = isReceived
      ? `${msg.sender_first_name} ${msg.sender_last_name}`
      : `${msg.recipient_first_name} ${msg.recipient_last_name}`
    const matchesSearch = msgSearch === '' ||
      otherName.toLowerCase().includes(msgSearch.toLowerCase()) ||
      (msg.subject || '').toLowerCase().includes(msgSearch.toLowerCase()) ||
      (msg.message || '').toLowerCase().includes(msgSearch.toLowerCase())
    if (!matchesSearch) return false
    if (msgFilter === 'inbox') return isReceived
    if (msgFilter === 'sent') return !isReceived
    if (msgFilter === 'unread') return isReceived && !msg.is_read
    return true
  })

  // Group contacts by role for the select dropdown
  const groupedContacts = contacts.reduce((acc, c) => {
    const group = c.role || 'Other'
    if (!acc[group]) acc[group] = []
    acc[group].push(c)
    return acc
  }, {})

  if (loading) return <LoadingSpinner className="py-8" label="Loading..." />

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-white">{title}</h1>
        <button
          onClick={() => { setReplyTo(null); setComposeData({ recipient_id: '', subject: '', message: '' }); setComposing(true) }}
          className="cinema-btn-primary flex items-center gap-2"
        >
          <Send className="w-4 h-4" />
          New Message
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        {[
          { key: 'notifications', icon: Bell, label: 'Notifications', badge: unreadNotifs },
          { key: 'messages', icon: MessageSquare, label: 'Messages', badge: unreadMsgs }
        ].map(({ key, icon: Icon, label, badge }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 font-medium text-sm border-b-2 ${
              tab === key ? 'border-cinema-glow text-cinema-glow' : 'border-transparent text-cinema-muted hover:text-white'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {badge > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] text-center">{badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Notifications Tab */}
      {tab === 'notifications' && (
        <div className="space-y-3">
          {unreadNotifs > 0 && (
            <div className="flex justify-end">
              <button onClick={markAllRead} className="text-sm text-cinema-glow hover:text-white flex items-center gap-1">
                <CheckCheck className="w-4 h-4" /> Mark all as read
              </button>
            </div>
          )}
          {notifications.length === 0 ? (
            <div className="cinema-glass border border-white/10 text-center py-12">
              <Bell className="w-12 h-12 text-cinema-muted/60 mx-auto mb-3" />
              <p className="text-cinema-muted/70">No notifications yet</p>
            </div>
          ) : (
            notifications.map((notif) => {
              const key = `notif-${notif.id}`
              const isOpen = expanded === key
              const Icon = TYPE_ICON[notif.type] || Info
              const colorClass = TYPE_COLOR[notif.type] || TYPE_COLOR.info
              return (
                <div
                  key={notif.id}
                  className={`cinema-glass border border-white/10 cursor-pointer ${notif.is_read ? 'opacity-75' : 'border-cinema-glow/30'}`}
                  onClick={() => toggleExpand(notif.id, 'notif')}
                >
                  <div className="flex items-start gap-3">
                    <span className={`p-2 flex-shrink-0 ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`font-medium text-white ${!notif.is_read ? 'font-semibold' : ''}`}>{notif.title}</p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!notif.is_read && <span className="w-2 h-2 bg-cinema-glow" />}
                          <span className="text-xs text-cinema-muted/60">{formatDate(notif.created_at)}</span>
                          {isOpen ? <ChevronUp className="w-4 h-4 text-cinema-muted/60" /> : <ChevronDown className="w-4 h-4 text-cinema-muted/60" />}
                        </div>
                      </div>
                      {!isOpen && <p className="text-sm text-cinema-muted/70 truncate mt-0.5">{notif.message}</p>}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="mt-3 pt-3 border-t border-white/10 text-sm text-cinema-muted whitespace-pre-wrap">{notif.message}</div>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Messages Tab */}
      {tab === 'messages' && (
        <div className="space-y-3">
          {/* Filter & Search */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-cinema-muted/60" />
              <input
                type="text" value={msgSearch}
                onChange={e => setMsgSearch(e.target.value)}
                placeholder="Search messages..."
                className="w-full px-4 py-2 min-h-[48px] text-sm pl-9 border border-white/10 bg-cinema-graphite text-white rounded-none focus:border-cinema-glow focus:ring-0"
              />
            </div>
            <div className="flex gap-1 bg-cinema-graphite p-1">
              {[['all', 'All'], ['inbox', 'Inbox'], ['sent', 'Sent'], ['unread', 'Unread']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setMsgFilter(val)}
                  className={`px-3 py-1 text-sm font-medium ${
                    msgFilter === val ? 'bg-cinema-glow text-white' : 'text-cinema-muted hover:text-white'
                  }`}
                >
                  {label}
                  {val === 'unread' && unreadMsgs > 0 && (
                    <span className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5">{unreadMsgs}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {filteredMessages.length === 0 ? (
            <div className="cinema-glass border border-white/10 text-center py-12">
              <Mail className="w-12 h-12 text-cinema-muted/60 mx-auto mb-3" />
              <p className="text-cinema-muted/70">{msgSearch || msgFilter !== 'all' ? 'No messages match your filter' : 'No messages yet'}</p>
            </div>
          ) : (
            filteredMessages.map((msg) => {
              const key = `msg-${msg.id}`
              const isOpen = expanded === key
              const isReceived = msg.recipient_id === user?.id
              const isUnread = isReceived && !msg.is_read
              const otherName = isReceived
                ? `${msg.sender_first_name} ${msg.sender_last_name}`
                : `${msg.recipient_first_name} ${msg.recipient_last_name}`
              return (
                <div
                  key={msg.id}
                  className={`cinema-glass border border-white/10 cursor-pointer ${isUnread ? 'border-cinema-glow/30' : 'opacity-80'}`}
                  onClick={() => toggleExpand(msg.id, 'msg')}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center text-sm font-bold flex-shrink-0 ${isReceived ? 'bg-cinema-glow/10 text-cinema-glow' : 'bg-cinema-graphite text-cinema-muted'}`}>
                      {otherName?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className={`text-sm ${isUnread ? 'font-semibold text-white' : 'font-medium text-cinema-muted'}`}>
                            {isReceived ? `From: ${otherName}` : `To: ${otherName}`}
                          </p>
                          <p className={`text-sm ${isUnread ? 'font-semibold text-white' : 'text-cinema-muted'}`}>{msg.subject}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isUnread && <span className="w-2 h-2 bg-cinema-glow" />}
                          <span className="text-xs text-cinema-muted/60">{formatDate(msg.created_at)}</span>
                          {isOpen ? <ChevronUp className="w-4 h-4 text-cinema-muted/60" /> : <ChevronDown className="w-4 h-4 text-cinema-muted/60" />}
                        </div>
                      </div>
                      {!isOpen && <p className="text-sm text-cinema-muted/60 truncate mt-0.5">{msg.message}</p>}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                      <p className="text-sm text-cinema-muted whitespace-pre-wrap">{msg.message}</p>
                      {isReceived && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleReply(msg) }}
                          className="mt-3 flex items-center gap-1 text-sm text-cinema-glow hover:text-white font-medium"
                        >
                          <Reply className="w-4 h-4" /> Reply
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Compose Modal */}
      {composing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="cinema-glass w-full max-w-md">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">
                  {replyTo ? 'Reply to Message' : 'New Message'}
                </h3>
                <button onClick={() => { setComposing(false); setReplyTo(null) }} className="text-cinema-muted/60 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSend} className="space-y-3">
                {!replyTo && (
                  <div>
                    <label className="block text-sm font-medium text-cinema-muted mb-1">To</label>
                    {contacts.length > 0 ? (
                      <select
                        value={composeData.recipient_id}
                        onChange={e => setComposeData({ ...composeData, recipient_id: e.target.value })}
                        className="w-full px-4 py-2 min-h-[48px] text-sm border border-white/10 bg-cinema-graphite text-white rounded-none focus:border-cinema-glow focus:ring-0" required
                      >
                        <option value="">Select recipient...</option>
                        {Object.entries(groupedContacts).map(([group, members]) => (
                          <optgroup key={group} label={group.charAt(0).toUpperCase() + group.slice(1)}>
                            {members.map(c => (
                              <option key={c.user_id} value={c.user_id}>
                                {c.first_name} {c.last_name}
                                {c.context && c.context !== group ? ` — ${c.context}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="number" placeholder="Recipient User ID"
                        value={composeData.recipient_id}
                        onChange={e => setComposeData({ ...composeData, recipient_id: e.target.value })}
                        className="w-full px-4 py-2 min-h-[48px] text-sm border border-white/10 bg-cinema-graphite text-white rounded-none focus:border-cinema-glow focus:ring-0"
                      />
                    )}
                  </div>
                )}
                {replyTo && (
                  <div className="bg-cinema-graphite p-3 text-sm text-cinema-muted">
                    Replying to: <span className="font-medium">{replyTo.sender_first_name} {replyTo.sender_last_name}</span>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-cinema-muted mb-1">Subject</label>
                  <input
                    type="text" value={composeData.subject}
                    onChange={e => setComposeData({ ...composeData, subject: e.target.value })}
                    className="w-full px-4 py-2 min-h-[48px] text-sm border border-white/10 bg-cinema-graphite text-white rounded-none focus:border-cinema-glow focus:ring-0" placeholder="Subject"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-cinema-muted mb-1">Message</label>
                  <textarea
                    rows={5} value={composeData.message}
                    onChange={e => setComposeData({ ...composeData, message: e.target.value })}
                    className="w-full px-4 py-2 min-h-[48px] text-sm border border-white/10 bg-cinema-graphite text-white rounded-none focus:border-cinema-glow focus:ring-0 resize-none" placeholder="Type your message..."
                    required
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="submit" disabled={sending} className="cinema-btn-primary flex items-center gap-2 flex-1">
                    <Send className="w-4 h-4" />
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                  <button type="button" onClick={() => { setComposing(false); setReplyTo(null) }} className="cinema-btn-ghost">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
